/**
 * TemplateHistoryTab — worked examples and the version changelog. Demo page: /tables/basic-tables (compact lists in
 * cards).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { Card, EmptyState } from '../../../components/ui/index.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import { StatusBadge, formatDate } from './detail-parts.js';

export const TemplateHistoryTab: React.FC<{ template: AgentTemplate }> = ({ template }) => {
  const examples = template.examples ?? [];
  const versions = template.versionHistory ?? [];
  return (
    <div className="grid items-start gap-5 xl:grid-cols-2">
      <Card title="Examples">
        {examples.length === 0 ? (
          <p className="text-sm text-muted">No worked examples are recorded.</p>
        ) : (
          <div className="space-y-3">
            {examples.map((example) => (
              <div key={example.title} className="rounded-lg border p-3">
                <h3 className="text-sm font-medium text-heading">{example.title}</h3>
                <pre className="mt-2 max-h-48 overflow-auto rounded-sm bg-surface-alt p-2 text-xs text-muted">
                  {JSON.stringify(example.input, null, 2)}
                </pre>
                <ul className="mt-2 list-inside list-disc text-xs text-muted">
                  {example.expectedOutput.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card
        title="Changelog"
        actions={
          <Link
            to={`/agents/templates/${template.id}/versions`}
            className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'xs' })}
          >
            Manage versions
          </Link>
        }
      >
        {versions.length === 0 ? (
          <EmptyState
            icon={History}
            title="No versions yet"
            description="Versions appear here once the template is saved as a version."
          />
        ) : (
          <ul className="space-y-3">
            {versions.slice(0, 8).map((version) => (
              <li
                key={version.id}
                className="flex items-start justify-between gap-3 border-b pb-3 last:border-0 last:pb-0"
              >
                <div>
                  <div className="text-sm font-medium text-heading">Version {version.version}</div>
                  <p className="text-xs text-muted">{version.changeSummary || 'No change summary.'}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <StatusBadge status={version.status} />
                  <span className="text-xs text-muted">{formatDate(version.promotedAt ?? version.createdAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};
